import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

/**
 * Page chrome — what the phone's top bar needs to name the page it is sitting
 * above: the title, and how to go back.
 *
 * On a phone the page is named ONCE, in the sticky top bar, rather than again in
 * a header block below it that cost a third of the screen. PageHeader is
 * rendered deep inside each of 34 pages and AppMobileTopbar is rendered by
 * AppShell above them all, so the title has to travel upward — hence a context
 * rather than a prop.
 *
 * ⚠️ THE TRAP THIS EXISTS TO AVOID. React mounts the incoming route's component
 * BEFORE it unmounts the outgoing one. So on every navigation the order is:
 *
 *     B publishes "Orders"  →  A's cleanup runs
 *
 * A naive `return () => setChrome(null)` therefore clears the title of the page
 * you just arrived at, and the bar goes blank until something else re-renders.
 * Every registration carries an id and a clear only lands if that id is STILL
 * the owner — so A's late cleanup is a no-op.
 */

const PageChromeContext = createContext(null);

export function PageChromeProvider({ children }) {
  const [chrome, setChrome] = useState(null);
  const owner = useRef(null);

  const publish = useCallback((id, value) => {
    owner.current = id;
    setChrome(value);
  }, []);

  const clear = useCallback((id) => {
    // Only the current owner may clear. See the note above.
    if (owner.current !== id) return;
    owner.current = null;
    setChrome(null);
  }, []);

  const value = useMemo(() => ({ chrome, publish, clear }), [chrome, publish, clear]);

  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
}

/** What the mobile top bar reads. Null outside a provider, and outside a page. */
export function usePageChrome() {
  return useContext(PageChromeContext)?.chrome ?? null;
}

/**
 * What PageHeader calls. Safe with no provider — the tests and the styleguide
 * render PageHeader on its own, and it must not need the whole shell.
 */
export function usePublishPageChrome(id, value) {
  const ctx = useContext(PageChromeContext);
  const publish = ctx?.publish;
  const clear = ctx?.clear;
  const { title, backTo, onBack, showBackButton } = value;

  React.useEffect(() => {
    if (!publish) return undefined;
    publish(id, { title, backTo, onBack, showBackButton });
    return () => clear?.(id);
  }, [id, title, backTo, onBack, showBackButton, publish, clear]);
}
